import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { googleRedirectUri } from "@/pages/admin/GoogleCallback";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const fmt = (s) => (s ? new Date(s).toLocaleString("tr-TR") : "—");
const DEFAULT_SITEMAP = `${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`;

function Step({ n, title, done, children, testid }) {
  return (
    <section className="bg-white border rounded-xl p-5 mb-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {done ? <CheckCircle2 className="w-5 h-5 text-teal-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
        <h2 className="font-head font-semibold text-slate-900">{n}. {title}</h2>
        <Badge variant="secondary" className="ml-auto text-[10px]" data-testid={`${testid}-status`}>{done ? "Tamam" : "Bekliyor"}</Badge>
      </div>
      <div className="text-sm text-slate-600 space-y-3">{children}</div>
    </section>
  );
}

function OAuthStep({ st, reload }) {
  const [cid, setCid] = useState(st.client_id || "");
  const [sec, setSec] = useState("");
  const save = () => api.put("/admin/google/oauth-config", { client_id: cid, client_secret: sec })
    .then(() => { toast.success("Kaydedildi"); setSec(""); reload(); }).catch(err);
  return (
    <Step n={1} title="Google Cloud OAuth bilgileri" done={st.oauth_yapilandirildi} testid="gsc-step-oauth">
      <ol className="list-decimal ml-5 space-y-1 text-xs">
        <li>console.cloud.google.com adresinde bir proje oluşturun.</li>
        <li>“API'ler ve Hizmetler → Kitaplık” bölümünden <b>Google Search Console API</b>, <b>Site Verification API</b> ve (reklam için) <b>Google Ads API</b>'yi etkinleştirin.</li>
        <li>“OAuth izin ekranı”nı yapılandırın (uygulama türü: Harici; test aşamasında kendi e-postanızı test kullanıcısı ekleyin).</li>
        <li>“Kimlik bilgileri → OAuth istemci kimliği → Web uygulaması” oluşturun ve yetkili yönlendirme URI'sine şunu ekleyin:
          <code className="block bg-slate-100 rounded px-2 py-1 mt-1 break-all" data-testid="gsc-redirect-uri">{googleRedirectUri()}</code>
          Canlı alan adınız için de aynı yolu (/yonetim/google/baglan) ekleyin.</li>
        <li>Oluşan İstemci Kimliği ve Gizli Anahtarı aşağıya girin. Gizli anahtar şifrelenerek saklanır ve tekrar gösterilmez.</li>
      </ol>
      <Input placeholder="İstemci Kimliği (Client ID)" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="gsc-client-id" />
      <Input type="password" placeholder={st.oauth_yapilandirildi ? "Gizli anahtar kayıtlı (değiştirmek için yazın)" : "İstemci Gizli Anahtarı (Client Secret)"}
        value={sec} onChange={(e) => setSec(e.target.value)} data-testid="gsc-client-secret" />
      <Button onClick={save} disabled={!cid} className="bg-teal-600 hover:bg-teal-700" data-testid="gsc-save-oauth">Kaydet</Button>
    </Step>
  );
}

function ConnectStep({ st, reload }) {
  const connect = () => api.post("/admin/google/auth-url", { redirect_uri: googleRedirectUri() })
    .then((r) => { window.location.href = r.data.url; }).catch(err);
  const disconnect = () => window.confirm("Google bağlantısı kaldırılsın mı?") &&
    api.post("/admin/google/disconnect").then(reload).catch(err);
  const missing = Object.entries(st.izinler || {}).filter(([, v]) => !v).map(([k]) => k);
  return (
    <Step n={2} title="Google Hesabını Bağla" done={st.bagli} testid="gsc-step-connect">
      {st.bagli ? (
        <>
          <p>Bağlı hesap: <b data-testid="gsc-account">{st.hesap}</b> · {fmt(st.bagli_tarih)}</p>
          {missing.length > 0 && (
            <p className="text-amber-700 text-xs flex gap-1" data-testid="gsc-missing-scopes">
              <AlertTriangle className="w-4 h-4 shrink-0" />Verilmeyen izinler: {missing.join(", ")}. İlgili özellikler çalışmaz; hesabı yeniden bağlayıp tüm izinleri onaylayın.
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={connect} data-testid="gsc-reconnect">Yeniden bağla</Button>
            <Button variant="outline" onClick={disconnect} data-testid="gsc-disconnect">Bağlantıyı kaldır</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs">Google'a yönlendirileceksiniz. Search Console, site doğrulama ve Google Ads izinlerini onaylayın.
            Google'da bu siteye sahip/yetkili olduğunuz hesabı seçin.</p>
          <Button onClick={connect} disabled={!st.oauth_yapilandirildi} className="bg-teal-600 hover:bg-teal-700" data-testid="gsc-connect">
            Google Hesabını Bağla</Button>
        </>
      )}
    </Step>
  );
}

function SiteStep({ st, reload }) {
  const [sites, setSites] = useState(null);
  const [site, setSite] = useState(st.site_url || "");
  const [token, setToken] = useState(null);
  useEffect(() => { if (st.bagli) api.get("/admin/gsc/sites").then((r) => setSites(r.data.items)).catch(err); }, [st.bagli]);
  const entry = sites?.find((s) => s.siteUrl === st.site_url);
  const verified = entry && entry.permissionLevel !== "siteUnverifiedUser";
  const saveSite = () => api.put("/admin/gsc/site", { site_url: site }).then(reload).catch(err);
  const getToken = () => api.post("/admin/gsc/verify-token").then((r) => setToken(r.data.token)).catch(err);
  const verify = () => api.post("/admin/gsc/verify").then(() => { toast.success("Doğrulandı"); reload(); window.location.reload(); }).catch(err);
  return (
    <Step n={3} title="Search Console mülkü ve doğrulama" done={!!verified} testid="gsc-step-site">
      {!st.bagli ? <p className="text-xs">Önce Google hesabını bağlayın.</p> : (
        <>
          {sites && sites.length > 0 && (
            <div className="text-xs">Hesaptaki mülkler:
              <ul className="mt-1 space-y-1">{sites.map((s) => (
                <li key={s.siteUrl}><button className="text-teal-700 underline" onClick={() => setSite(s.siteUrl)}>{s.siteUrl}</button>
                  {" "}<Badge variant="secondary" className="text-[10px]">{s.permissionLevel}</Badge></li>))}</ul>
            </div>
          )}
          <p className="text-xs">Alan adı mülkü için <code>sc-domain:ornek.com</code>, adres önekli mülk için <code>https://ornek.com/</code> biçiminde girin.</p>
          <div className="flex gap-2">
            <Input value={site} onChange={(e) => setSite(e.target.value)} placeholder="sc-domain:alanadiniz.com" data-testid="gsc-site-input" />
            <Button variant="outline" onClick={saveSite} disabled={!site} data-testid="gsc-site-save">Seç</Button>
          </div>
          {st.site_url && (verified ? (
            <p className="text-teal-700 text-xs" data-testid="gsc-verified">“{st.site_url}” mülkü Google tarafından doğrulanmış ({entry.permissionLevel}).</p>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-2" data-testid="gsc-verify-box">
              <p><b>Doğrulama gerekli.</b> Site adresini girmek sahiplik kanıtı değildir. Alan adınızın DNS yönetim paneline (alan adını aldığınız firma) bir TXT kaydı ekleyin:</p>
              <Button size="sm" variant="outline" onClick={getToken} data-testid="gsc-get-token">DNS TXT kaydını oluştur</Button>
              {token && <code className="block bg-white border rounded px-2 py-1 break-all" data-testid="gsc-token">{token}</code>}
              <p>Kaydı ekledikten sonra yayılması birkaç dakika–birkaç saat sürebilir. Ardından “Doğrula”ya basın.</p>
              <Button size="sm" onClick={verify} className="bg-teal-600 hover:bg-teal-700" data-testid="gsc-verify">Doğrula</Button>
              <p className="text-slate-500">Not: Önizleme alan adları (emergentagent.com vb.) sizin DNS yönetiminizde olmadığından doğrulanamaz; kendi alan adınızı kullanın.</p>
            </div>
          ))}
        </>
      )}
    </Step>
  );
}

function SitemapStep({ ready, site }) {
  const [items, setItems] = useState([]);
  const SITEMAP = site?.startsWith("http") ? `${site.replace(/\/$/, "")}/api/sitemap.xml` : DEFAULT_SITEMAP;
  const load = () => api.get("/admin/gsc/sitemaps").then((r) => setItems(r.data.items)).catch(err);
  useEffect(() => { if (ready) load(); }, [ready]);
  const submit = () => api.post("/admin/gsc/sitemaps").then(() => { toast.success("Gönderildi"); load(); }).catch(err);
  const sent = items.some((s) => s.path === SITEMAP);
  return (
    <Step n={4} title="Site haritası gönderimi" done={sent} testid="gsc-step-sitemap">
      <p className="text-xs">Site haritası: <code className="break-all">{SITEMAP}</code></p>
      <Button onClick={submit} disabled={!ready} className="bg-teal-600 hover:bg-teal-700" data-testid="gsc-submit-sitemap">Google'a gönder</Button>
      {items.map((s) => (
        <div key={s.path} className="text-xs border rounded p-2" data-testid="gsc-sitemap-item">
          <p className="break-all font-medium">{s.path}</p>
          <p>Gönderim: {fmt(s.lastSubmitted)} · Son okuma: {fmt(s.lastDownloaded)} · {s.isPending ? "İşleniyor" : "İşlendi"} · Hata: {s.errors || 0} · Uyarı: {s.warnings || 0}</p>
        </div>
      ))}
    </Step>
  );
}

function Performance({ ready }) {
  const [days, setDays] = useState(28);
  const [d, setD] = useState(null);
  const load = (refresh = false) => api.get(`/admin/gsc/performance?days=${days}&refresh=${refresh}`).then((r) => setD(r.data)).catch(err);
  useEffect(() => { if (ready) load(); }, [ready, days]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ready) return null;
  const t = d?.toplam || {};
  const Table = ({ rows, label }) => (
    <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-slate-500 text-left">
      <th className="py-1">{label}</th><th>Tıklama</th><th>Gösterim</th><th>TO</th><th>Ort. sıra</th></tr></thead>
      <tbody>{(rows || []).slice(0, 20).map((r) => (<tr key={r.keys[0]} className="border-t">
        <td className="py-1 pr-2 break-all">{r.keys[0]}</td><td>{r.clicks}</td><td>{r.impressions}</td>
        <td>{(r.ctr * 100).toFixed(1)}%</td><td>{r.position.toFixed(1)}</td></tr>))}</tbody></table>
      {!rows?.length && <p className="text-slate-400 text-xs py-2">Bu dönem için veri yok.</p>}</div>
  );
  return (
    <section className="bg-white border rounded-xl p-5 mb-4" data-testid="gsc-performance">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-head font-semibold text-slate-900">Arama performansı</h2>
        {[7, 28, 90].map((n) => <Button key={n} size="sm" variant={days === n ? "default" : "outline"} onClick={() => setDays(n)}>{n} gün</Button>)}
        <Button size="sm" variant="outline" onClick={() => load(true)} data-testid="gsc-perf-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Yenile</Button>
        <span className="text-xs text-slate-500 ml-auto" data-testid="gsc-perf-updated">Son güncelleme: {fmt(d?.guncellendi)}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
        <div>Tıklama<br /><b className="text-xl">{t.clicks ?? 0}</b></div>
        <div>Gösterim<br /><b className="text-xl">{t.impressions ?? 0}</b></div>
        <div>Tıklama oranı<br /><b className="text-xl">{t.ctr ? (t.ctr * 100).toFixed(1) : 0}%</b></div>
        <div>Ort. sıra<br /><b className="text-xl">{t.position ? t.position.toFixed(1) : "—"}</b></div>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Google verileri 2–3 gün gecikmeli olabilir; gizlilik nedeniyle bazı sorgular Google tarafından gösterilmez.</p>
      <div className="grid md:grid-cols-2 gap-4"><Table rows={d?.sorgular} label="Sorgu" /><Table rows={d?.sayfalar} label="Sayfa" /></div>
    </section>
  );
}

function Inspect({ ready }) {
  const [url, setUrl] = useState(window.location.origin + "/");
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/gsc/inspections").then((r) => setItems(r.data.items));
  useEffect(() => { if (ready) load(); }, [ready]);
  if (!ready) return null;
  const run = () => api.post("/admin/gsc/inspect", { url }).then(load).catch(err);
  return (
    <section className="bg-white border rounded-xl p-5" data-testid="gsc-inspect">
      <h2 className="font-head font-semibold text-slate-900 mb-1">İndeksleme kontrolü (URL Denetimi)</h2>
      <p className="text-xs text-slate-500 mb-3">Google API'si tüm site için “Sayfalar” raporunu vermez; seçtiğiniz adreslerin durumu tek tek sorgulanır (günlük kota sınırlıdır).</p>
      <div className="flex gap-2 mb-3"><Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="gsc-inspect-url" />
        <Button variant="outline" onClick={run} data-testid="gsc-inspect-run">Kontrol et</Button></div>
      {items.map((i) => (
        <div key={i.url} className="text-xs border rounded p-2 mb-2">
          <p className="break-all font-medium">{i.url}</p>
          <p>Sonuç: <b>{i.verdict}</b> · {i.coverageState} · robots: {i.robotsTxtState} · erişim: {i.pageFetchState}</p>
          <p>Son tarama: {fmt(i.lastCrawlTime)} · Google canonical: <span className="break-all">{i.googleCanonical || "—"}</span> · Kontrol: {fmt(i.kontrol_tarihi)}</p>
        </div>
      ))}
    </section>
  );
}

export default function AdminGoogle() {
  const [st, setSt] = useState(null);
  const [verified, setVerified] = useState(false);
  const load = () => api.get("/admin/google/status").then((r) => setSt(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (st?.bagli && st.site_url) api.get("/admin/gsc/sites").then((r) => {
      const e = r.data.items.find((s) => s.siteUrl === st.site_url);
      setVerified(!!e && e.permissionLevel !== "siteUnverifiedUser");
    }).catch(() => setVerified(false));
  }, [st]);
  if (!st) return <p className="text-slate-400">Yükleniyor...</p>;
  return (
    <div className="max-w-4xl" data-testid="admin-gsc-page">
      <h1 className="font-head text-2xl font-bold text-slate-900 mb-1">Search Console</h1>
      <p className="text-sm text-slate-500 mb-4">Kurulum sihirbazı. Durum bilgileri yalnızca Google API'sinden gelen yanıtlara göre gösterilir.</p>
      {!st.oauth_yapilandirildi && <p className="bg-slate-100 border rounded-lg p-3 text-sm mb-4" data-testid="gsc-not-configured">Google bağlantısı yapılandırılmadı.</p>}
      <OAuthStep st={st} reload={load} />
      <ConnectStep st={st} reload={load} />
      <SiteStep st={st} reload={load} />
      <SitemapStep ready={verified} site={st.site_url} />
      <Performance ready={verified} />
      <Inspect ready={verified} />
    </div>
  );
}
